// src/components/contacts/ContactForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, Save, Person, Business } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import {
  CONTACT_TYPES,
  CONTACT_TYPE_LABELS,
  COMPANY_ONLY_TYPES,
  PERSON_TYPES,
} from "../../utils/models/contactModel";
import { CLIENT_TYPES, ENTRY_CHANNELS } from "../../utils/models/clientModel";
import { createCompany } from "../../utils/models/companyModel";

const ContactForm = ({ contact, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { add: addContact, update: updateContact } =
    useAuditFirestore("contacts");
  const { add: addClient, update: updateClient } = useAuditFirestore("clients");
  const { add: addCompany } = useAuditFirestore("companies");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    contactType: contact?.contactType || CONTACT_TYPES.CLIENT,
    firstName: contact?.firstName || "",
    lastName: contact?.lastName || "",
    email: contact?.email || "",
    phone: contact?.phone || "",
    mobile: contact?.mobile || "",
    dni: contact?.dni || "",
    contactName: contact?.contactName || "",
    position: contact?.position || "",
    department: contact?.department || "",
    companyName: contact?.companyName || "",
    companyId: contact?.companyId || "",
    companyTaxId: contact?.companyTaxId || "",
    companyIndustry: contact?.companyIndustry || "",
    companyWebsite: contact?.companyWebsite || "",
    supplierContactName: contact?.supplierContactName || "",
    supplierContactPhone: contact?.supplierContactPhone || "",
    supplierContactEmail: contact?.supplierContactEmail || "",
    representativeName: contact?.representativeName || "",
    representativePhone: contact?.representativePhone || "",
    representativeEmail: contact?.representativeEmail || "",
    representativeDni: contact?.representativeDni || "",
    clientType: contact?.clientType || CLIENT_TYPES.INDIVIDUAL,
    clientId: contact?.clientId || "",
    supplierId: contact?.supplierId || "",
    commercialId: contact?.commercialId || "",
    address: {
      street: contact?.address?.street || "",
      city: contact?.address?.city || "",
      state: contact?.address?.state || "",
      zipCode: contact?.address?.zipCode || "",
      country: contact?.address?.country || "España",
    },
    notes: contact?.notes || "",
  });

  const isClient = formData.contactType === CONTACT_TYPES.CLIENT;
  const isSupplier = formData.contactType === CONTACT_TYPES.SUPPLIER;
  const isEPC = formData.contactType === CONTACT_TYPES.EPC;
  const isCommercial = formData.contactType === CONTACT_TYPES.COMMERCIAL;
  const isCompanyOnly = COMPANY_ONLY_TYPES.includes(formData.contactType);
  const isPersonType = PERSON_TYPES.includes(formData.contactType);
  const { add: addSupplier } = useAuditFirestore("suppliers");
const { add: addCommercial } = useAuditFirestore("commercials");
  const isClientCompany =
    isClient && formData.clientType === CLIENT_TYPES.COMPANY;
  const isClientIndividual =
    isClient && formData.clientType === CLIENT_TYPES.INDIVIDUAL;
  const showPersonalData = isPersonType || isClientIndividual;

  useEffect(() => {
    setFormData({
      contactType: contact?.contactType || CONTACT_TYPES.CLIENT,
      firstName: contact?.firstName || "",
      lastName: contact?.lastName || "",
      email: contact?.email || "",
      phone: contact?.phone || "",
      mobile: contact?.mobile || "",
      dni: contact?.dni || "",
      contactName: contact?.contactName || "",
      position: contact?.position || "",
      department: contact?.department || "",
      companyName: contact?.companyName || "",
      companyId: contact?.companyId || "",
      companyTaxId: contact?.companyTaxId || "",
      companyIndustry: contact?.companyIndustry || "",
      companyWebsite: contact?.companyWebsite || "",
      supplierContactName: contact?.supplierContactName || "",
      supplierContactPhone: contact?.supplierContactPhone || "",
      supplierContactEmail: contact?.supplierContactEmail || "",
      representativeName: contact?.representativeName || "",
      representativePhone: contact?.representativePhone || "",
      representativeEmail: contact?.representativeEmail || "",
      representativeDni: contact?.representativeDni || "",
      clientType: contact?.clientType || CLIENT_TYPES.INDIVIDUAL,
      clientId: contact?.clientId || "",
      supplierId: contact?.supplierId || "",
      commercialId: contact?.commercialId || "",
      address: {
        street: contact?.address?.street || "",
        city: contact?.address?.city || "",
        state: contact?.address?.state || "",
        zipCode: contact?.address?.zipCode || "",
        country: contact?.address?.country || "España",
      },
      notes: contact?.notes || "",
    });
  }, [contact]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (error) setError("");
  };

  const handleClientType = (_, value) => {
    if (!value) return;
    setFormData((prev) => ({ ...prev, clientType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSupplier && !formData.companyName) {
      setError("El nombre de la empresa es obligatorio para proveedores");
      return;
    }
    if (showPersonalData && (!formData.firstName || !formData.lastName)) {
      setError("Nombre y apellidos son obligatorios");
      return;
    }
    if (isClientCompany && !formData.companyName) {
      setError("El nombre de la empresa es obligatorio");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let companyId = formData.companyId;

      if ((isSupplier || isClientCompany) && !companyId) {
        const companyData = createCompany({
          name: formData.companyName,
          taxId: formData.companyTaxId || "",
          industry: formData.companyIndustry || "",
          website: formData.companyWebsite || "",
          email: formData.email || "",
          phone: formData.phone || "",
          address: formData.address,
          notes: `Empresa creada desde contacto: ${formData.companyName}`,
        });
        const newCompany = await addCompany(companyData);
        companyId = newCompany?.id || newCompany;
      }

      const contactData = {
        contactType: formData.contactType,
        firstName: showPersonalData ? formData.firstName : "",
        lastName: showPersonalData ? formData.lastName : "",
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile,
        dni: showPersonalData ? formData.dni : "",
        contactName: isClientCompany ? formData.contactName : "",
        position: isPersonType ? formData.position : "",
        department: isPersonType ? formData.department : "",
        companyId: companyId || formData.companyId || "",
        companyName: isSupplier || isClientCompany ? formData.companyName : "",
        companyTaxId: isSupplier
          ? formData.companyTaxId
          : isClientCompany
            ? formData.companyTaxId
            : "",
        companyIndustry: isSupplier ? formData.companyIndustry : "",
        companyWebsite: isSupplier ? formData.companyWebsite : "",
        supplierContactName: isSupplier ? formData.supplierContactName : "",
        supplierContactPhone: isSupplier ? formData.supplierContactPhone : "",
        supplierContactEmail: isSupplier ? formData.supplierContactEmail : "",
        representativeName: isClientCompany ? formData.representativeName : "",
        representativePhone: isClientCompany
          ? formData.representativePhone
          : "",
        representativeEmail: isClientCompany
          ? formData.representativeEmail
          : "",
        representativeDni: isClientCompany ? formData.representativeDni : "",
        clientType: isClient ? formData.clientType : CLIENT_TYPES.INDIVIDUAL,
        clientId: formData.clientId || "",
        supplierId: formData.supplierId || "",
        commercialId: formData.commercialId || "",
        address: formData.address,
        notes: formData.notes,
        updatedAt: new Date().toISOString(),
      };

      let savedContactId = contact?.id;

      if (contact?.id) {
        await updateContact(contact.id, contactData);
      } else {
        const result = await addContact({
          ...contactData,
          createdAt: new Date().toISOString(),
        });
        savedContactId = result?.id || result;
      }

      if (isClient) {
        const clientData = {
          type: formData.clientType,
          firstName:
            formData.clientType === CLIENT_TYPES.INDIVIDUAL
              ? formData.firstName
              : "",
          lastName:
            formData.clientType === CLIENT_TYPES.INDIVIDUAL
              ? formData.lastName
              : "",
          email: formData.email,
          phone: formData.phone,
          dni: formData.dni,
          contactName:
            formData.clientType === CLIENT_TYPES.COMPANY
              ? formData.contactName
              : "",
          companyName:
            formData.clientType === CLIENT_TYPES.COMPANY
              ? formData.companyName
              : "",
          taxId:
            formData.clientType === CLIENT_TYPES.COMPANY
              ? formData.companyTaxId
              : "",
          companyId:
            formData.clientType === CLIENT_TYPES.COMPANY ? companyId : null,
          representativeName:
            formData.clientType === CLIENT_TYPES.COMPANY
              ? formData.representativeName
              : "",
          representativePhone:
            formData.clientType === CLIENT_TYPES.COMPANY
              ? formData.representativePhone
              : "",
          representativeEmail:
            formData.clientType === CLIENT_TYPES.COMPANY
              ? formData.representativeEmail
              : "",
          representativeDni:
            formData.clientType === CLIENT_TYPES.COMPANY
              ? formData.representativeDni
              : "",
          address: formData.address,
          entryChannel: ENTRY_CHANNELS.WEB,
          notes: formData.notes,
          contactId: savedContactId,
          isActive: true,
          updatedAt: new Date().toISOString(),
        };

        if (formData.clientId) {
          await updateClient(formData.clientId, clientData);
        } else {
          const newClient = await addClient({
            ...clientData,
            createdAt: new Date().toISOString(),
          });
          const newClientId = newClient?.id || newClient;
          if (savedContactId && newClientId) {
            await updateContact(savedContactId, { clientId: newClientId });
          }
        }
      }

      // Después del bloque if (isClient) { ... }

      // NUEVO: Si es supplier, crear el supplier automáticamente
      if (isSupplier && !formData.supplierId) {
        const supplierData = {
          companyName: formData.companyName,
          taxId: formData.companyTaxId || "",
          industry: formData.companyIndustry || "",
          website: formData.companyWebsite || "",
          contactName: formData.supplierContactName || "",
          contactPhone: formData.supplierContactPhone || "",
          contactEmail: formData.supplierContactEmail || "",
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          contactId: savedContactId,
          companyId: companyId || formData.companyId || "",
          notes: formData.notes,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const newSupplier = await addSupplier({
          ...supplierData,
          createdAt: new Date().toISOString(),
        });
        const newSupplierId = newSupplier?.id || newSupplier;

        if (savedContactId && newSupplierId) {
          await updateContact(savedContactId, { supplierId: newSupplierId });
        }
      }

      // NUEVO: Si es commercial, crear el commercial automáticamente
      if (isCommercial && !formData.commercialId) {
        const commercialData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          dni: formData.dni || "",
          email: formData.email,
          phone: formData.phone,
          mobile: formData.mobile || "",
          position: formData.position || "",
          department: formData.department || "",
          address: formData.address,
          contactId: savedContactId,
          notes: formData.notes,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const newCommercial = await addCommercial({
          ...commercialData,
          createdAt: new Date().toISOString(),
        });
        const newCommercialId = newCommercial?.id || newCommercial;

        if (savedContactId && newCommercialId) {
          await updateContact(savedContactId, {
            commercialId: newCommercialId,
          });
        }
      }

      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
      "& fieldset": {
        borderColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.23)"
            : "rgba(0, 0, 0, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
    borderBottom: "2px solid",
    borderColor: theme.palette.primary.main,
    pb: 1,
    display: "inline-block",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.12)"
            : "rgba(0, 95, 101, 0.12)",
        background: theme.palette.background.paper,
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {contact?.id ? "Editar contacto" : "Nuevo contacto"}
        </Typography>
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 2, sm: 3 },
            borderRadius: "12px",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* TIPO DE CONTACTO */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <FormControl
            fullWidth
            size="small"
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: isClient ? "220px" : "300px" },
            }}
          >
            <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              Tipo de contacto
            </InputLabel>
            <Select
              name="contactType"
              value={formData.contactType}
              onChange={handleChange}
              label="Tipo de contacto"
              sx={{
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: { xs: 40, sm: 44 },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
                <MenuItem
                  key={value}
                  value={value}
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isClient && (
            <ToggleButtonGroup
              exclusive
              value={formData.clientType}
              onChange={handleClientType}
              size={isMobile ? "small" : "medium"}
              sx={{
                height: { xs: 40, sm: 44 },
                background: theme.palette.background.paper,
                borderRadius: "12px",
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
            >
              <ToggleButton
                value={CLIENT_TYPES.INDIVIDUAL}
                sx={{ flex: 1, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                <Person sx={{ mr: 1, fontSize: { xs: 16, sm: 18 } }} />
                Particular
              </ToggleButton>
              <ToggleButton
                value={CLIENT_TYPES.COMPANY}
                sx={{ flex: 1, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                <Business sx={{ mr: 1, fontSize: { xs: 16, sm: 18 } }} />
                Empresa
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* CLIENTE PARTICULAR */}
        {isClientIndividual && (
          <>
            <Typography sx={sectionTitleSx}>Datos personales</Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Nombre"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                required
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Apellidos"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                required
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="DNI / NIE"
                name="dni"
                value={formData.dni}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </>
        )}

        {/* CLIENTE EMPRESA */}
        {isClientCompany && (
          <>
            <Typography sx={sectionTitleSx}>Datos de empresa</Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Nombre de empresa"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 2,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                required
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="CIF / NIF"
                name="companyTaxId"
                value={formData.companyTaxId}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>

            <Typography sx={sectionTitleSx}>Representante</Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Nombre del representante"
                name="representativeName"
                value={formData.representativeName}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "250px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Teléfono"
                name="representativePhone"
                value={formData.representativePhone}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email"
                name="representativeEmail"
                value={formData.representativeEmail}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "250px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </>
        )}

        {/* SUPPLIER */}
        {isSupplier && (
          <>
            <Typography sx={sectionTitleSx}>Datos del proveedor</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.5, sm: 2.5 },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre de la empresa"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "300px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="CIF / NIF"
                  name="companyTaxId"
                  value={formData.companyTaxId}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Industria / Sector"
                  name="companyIndustry"
                  value={formData.companyIndustry}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Sitio web"
                  name="companyWebsite"
                  value={formData.companyWebsite}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
              </Box>
            </Box>

            <Typography sx={sectionTitleSx}>Persona de contacto</Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Nombre"
                name="supplierContactName"
                value={formData.supplierContactName}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "250px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Teléfono"
                name="supplierContactPhone"
                value={formData.supplierContactPhone}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email"
                name="supplierContactEmail"
                value={formData.supplierContactEmail}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "250px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </>
        )}

        {/* EPC / COMMERCIAL */}
        {isPersonType && (
          <>
            <Typography sx={sectionTitleSx}>
              {isEPC ? "Datos del miembro EPC" : "Datos del comercial"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Nombre"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                required
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Apellidos"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                required
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="DNI / NIE"
                name="dni"
                value={formData.dni}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </>
        )}

        {/* DATOS DE CONTACTO (COMÚN) */}
        <Typography sx={sectionTitleSx}>Datos de contacto</Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          {isClientCompany && (
            <Box sx={{ width: "100%", mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Nombre de la persona de contacto"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                sx={{ ...fieldSx, maxWidth: { xs: "100%", sm: "400px" } }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          )}
          <TextField
            fullWidth
            size="small"
            type="email"
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Teléfono"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
        </Box>

        {/* DATOS PROFESIONALES */}
        {isPersonType && (
          <>
            <Typography sx={sectionTitleSx}>Datos profesionales</Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Cargo"
                name="position"
                value={formData.position}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "250px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Departamento"
                name="department"
                value={formData.department}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "250px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </>
        )}

        {/* DIRECCIÓN */}
        <Typography sx={sectionTitleSx}>Dirección</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2.5 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Calle"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Ciudad"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Provincia"
              name="address.state"
              value={formData.address.state}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Código postal"
              name="address.zipCode"
              value={formData.address.zipCode}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="País"
              name="address.country"
              value={formData.address.country}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>
        </Box>

        {/* NOTAS */}
        <Typography sx={sectionTitleSx}>Notas internas</Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notas"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              background: theme.palette.background.paper,
            },
          }}
        />

        {/* FOOTER */}
        <Divider
          sx={{
            mt: { xs: 4, sm: 5 },
            mb: { xs: 2, sm: 3 },
            borderColor: theme.palette.divider,
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<Save />}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              boxShadow: "none",
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            {loading
              ? "Guardando..."
              : contact?.id
                ? "Actualizar"
                : "Crear contacto"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ContactForm;
